import { Button, FormControlLabel, InputAdornment, Paper, Radio, RadioGroup, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box, Theme, SxProps } from "@mui/system";
import { CSSProperties, Fragment, useEffect, useState } from "react";
import { betweenValuesValidationFn, falsyValidationFn, greaterThanValueValidationFn, isValidPhone, mailValidationFn, parseInvalidFields, phoneValidationFn } from "../utils/ValidationUtils";
import { GbkFieldType, GobikesField, GobikesFieldProps } from "../utils/GobikesField";
import { GobikesApi } from "../../config/Apis";
import { AccessoryModel, BikeModel, OneBike, PaymentMode, VendorPaymentMode, BookingType, BillingCycle, OrderUploadLabels } from "../utils/Models";
import { useSelector } from "react-redux";
import { getAuthCookie } from "../auth/AuthUtil";
import { getHubSelection } from "../../features/hubSelection";
import { getVendorProfile } from "../../features/vendorProfile";
import { parseErrorForAxios, roundAndToFixed2, computeBillingCycleEnd, billingCycleDisplayLabel } from "../utils/CommonUtils";
import { DateTime } from "luxon";
import { Colors, CountryCodesList, DefaultCountryCode } from "../../config/Constants";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { ArrowBack, Cancel, Close, DoneAll } from "@mui/icons-material";

// Constants and types
interface CustomerDetails {
  firstName?: string,
  lastName?: string,
  phone?: number,
  countryCode?: string,
  email?: string
  error?: string,
  enableNext?: boolean
}
interface BookingDetails {
  bookingChannel?: string;
  bookingChannelOther?: string;
  bookingType?: BookingType;
  billingCycle?: BillingCycle;
  startDate?: number;
  endDate?: number;
  quantity?: number;
  availableQuantity?: number;
  bikeId?: number;
  rcData?: {
    odStart?: number;
    rcNumber?: string;
  }[],
  accessoryData?: {
    id?: number,
    quantity?: number
  }[]
  emContact?: string;
  localAddress?: string;
  error?: string;
  enableNext?: boolean;
}
interface PaymentDetails {
  bikeRental: number;
  accessoryRental: number;
  deposit: number;
  defaultDiscount: number;
  vendorDiscount: number;
  finalAmountPaid?: number;
  convFees?: number;
  goBikesCommission?: number;
  finalDeductions?: number;
  finalPayableAmount?: number;
  paymentMethod?: PaymentMode;
  enableNext?: boolean;
  depositManuallySet?: boolean;
}
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  activeStep: number;
  formData: {
    customer: CustomerDetails,
    booking: BookingDetails,
    payment: PaymentDetails,
    uploads: { [k in OrderUploadLabels]?: string },
    extraUploadNames: { [OrderUploadLabels.EXTRA_1]?: string, [OrderUploadLabels.EXTRA_2]?: string },
    extraUploadCount: number
  };
  bikeListLoadRequired?: boolean;
  bikeListData?: BikeModel[];
  bikeListMap?: { [k: number]: BikeModel };
  accessoryListLoadRequired?: boolean;
  accessoryListData?: AccessoryModel[];
  accessoryListMap?: { [k: number]: AccessoryModel };
  calculationLoadRequired?: boolean;
  calculationData?: {
    bikeRental: number;
    accessoryRental: number;
    deposit: number;
    defaultDiscount: number;

  }
}
type PaymentDetailsFieldInfo = {
  name: string,
  type: GbkFieldType.STRING | GbkFieldType.NUMBER | GbkFieldType.DATE | GbkFieldType.CHECKBOX | GbkFieldType.DATE_TIME | GbkFieldType.DATE_TIME | GbkFieldType.SELECT,
  selectOptions: { value: string, display: string }[]
};
const bookingChannelOptions = [
  { value: 'GOWHEELO', display: 'GoWheelo' },
  { value: 'OTHER', display: 'Other' }
];
const bookingTypeOptions = [
  { value: BookingType.STANDARD, display: 'Standard Rental' },
  { value: BookingType.SUBSCRIPTION, display: 'Subscription Rental' }
];
const billingCycleOptions = Object.values(BillingCycle).map(c => ({ value: c, display: billingCycleDisplayLabel[c] }));

const steps = ['Customer Details', 'Vehicle Details', 'Booking Details', 'Uploads', 'Overview', 'Payment Details'];
const StepIndex = { CUSTOMER_DETAILS: 0, VEHICLE_DETAILS: 1, BOOKING_DETAILS: 2, UPLOADS: 3, OVERVIEW: 4, PAYMENT_DETAILS: 5 };

const MANDATORY_PICKUP_PHOTO_LABELS = [OrderUploadLabels.CUSTOMER, OrderUploadLabels.BIKE_LEFT, OrderUploadLabels.BIKE_RIGHT, OrderUploadLabels.BIKE_FRONT, OrderUploadLabels.BIKE_BACK, OrderUploadLabels.ODOMETER];

// Individual booking details
export function CreateBooking(props: { backFn: (reload: boolean) => void, drawerWidth: number }) {

  // Local state
  const [localState, setLocalState] = useState<LocalState>({
    activeStep: StepIndex.CUSTOMER_DETAILS,
    formData: { customer: { countryCode: DefaultCountryCode }, booking: {}, payment: { bikeRental: 0, accessoryRental: 0, defaultDiscount: 0, vendorDiscount: 0, deposit: 0, paymentMethod: PaymentMode.ONLINE }, uploads: {}, extraUploadNames: {}, extraUploadCount: 0 },
    bikeListLoadRequired: true,
    accessoryListLoadRequired: true
  });
  const [personalDetailsState, setPersonalDetailsState] = useState<{ hidden?: boolean, loading?: boolean }>({ hidden: true });
  const [otpState, setOtpState] = useState<{ userExists?: boolean, sent?: boolean, verified?: boolean, loading?: boolean, value?: string, error?: string }>({});
  const [emContactOtpState, setEmContactOtpState] = useState<{ phone?: string, sent?: boolean, verified?: boolean, loading?: boolean, value?: string, error?: string }>({});

  // Global state
  const selectedHub = useSelector(getHubSelection);
  const profile = useSelector(getVendorProfile);

  // Parse state and consts
  const authCookie = getAuthCookie();
  const allBikeRcData = localState?.bikeListMap?.[localState.formData.booking.bikeId || 0]?.hubs?.[0]?.hub_bikes?.bikes || [];
  const validRcSelectOptions = allBikeRcData.map(rc => ({ value: rc.rcNumber, display: rc.rcNumber, invisible: rc.used }));
  const usedAccessoryIds = new Set((localState.formData.booking.accessoryData || []).map(a => a.id));
  const validAccessoryOptions = (localState.accessoryListData || []).map(a => ({ value: a.id, display: a.name, invisible: usedAccessoryIds.has(a.id) }))
  const canAddMoreAccessories = (localState.formData.booking.accessoryData || []).length < (localState.accessoryListData || []).length;
  const selectedHubBike = localState?.bikeListMap?.[localState.formData.booking.bikeId || 0]?.hubs?.[0]?.hub_bikes;
  const vehicleDetailsValid = Boolean(
    localState.formData.booking.startDate &&
    localState.formData.booking.endDate &&
    localState.formData.booking.bikeId &&
    localState.formData.booking.quantity &&
    (localState.formData.booking.rcData || []).length === localState.formData.booking.quantity &&
    (localState.formData.booking.rcData || []).every(rc => rc.rcNumber && rc.odStart)
  );
  const emContactVerified = Boolean(emContactOtpState.verified && emContactOtpState.phone === localState.formData.booking.emContact);
  const bookingDetailsValid = Boolean(
    localState.formData.booking.bookingChannel &&
    (localState.formData.booking.bookingChannel !== 'OTHER' || localState.formData.booking.bookingChannelOther) &&
    (localState.formData.booking.accessoryData || []).every(a => a.id && a.quantity) &&
    (!profile.mandatoryEmContact || emContactVerified)
  );
  // Payment mode is chosen in a later step than Uploads, so it can't be used to decide this here -
  // gate purely on the vendor flag; the backend still skips the requirement for online-payment bookings
  const requiresMandatoryPickupPhotos = Boolean(profile.mandatoryPickupPhotos);
  const missingMandatoryUploadLabels = requiresMandatoryPickupPhotos
    ? MANDATORY_PICKUP_PHOTO_LABELS.filter(label => !localState.formData.uploads[label])
    : [];
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Available bike list
  const getAvailableBikesList = () => {
    if (localState.bikeListLoadRequired && localState.formData.booking.startDate && localState.formData.booking.endDate) {
      (async () => {
        try {
          const { data } = await GobikesApi.get(`bikes/all`, {
            headers: { 'vendor-token': authCookie },
            params: {
              city: profile.cityId,
              from: localState.formData.booking.startDate,
              to: localState.formData.booking.endDate,
              minimalData: true, ignoreMinDuration: true, ignoreOpTime: true, ignorePrepTime: true, hubs: selectedHub.id, quantity: true
            }
          })
          const bikeList: BikeModel[] = data.data.bikes;
          const bikeListMap = Object.fromEntries(bikeList.map(e => [e.id, e]))
          setLocalState({ ...localState, bikeListData: bikeList, bikeListLoadRequired: false, loading: false, bikeListMap, formData: { ...localState.formData, booking: { ...localState.formData.booking, bikeId: undefined, quantity: undefined, availableQuantity: undefined, rcData: undefined } } });
        } catch (e) {
          setLocalState({ ...localState, error: true, message: parseErrorForAxios(e) });
        }
      })();
    }
  }
  useEffect(getAvailableBikesList, [localState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Available accessory list
  const getAvailableAccessoriesList = () => {
    if (localState.accessoryListLoadRequired) {
      (async () => {
        try {
          const { data } = await GobikesApi.get(`bikes/accessory-classes`, {
            headers: { 'vendor-token': authCookie },
            params: {
              hub: selectedHub.id,
            }
          })
          const accessoryList: AccessoryModel[] = data.data;
          const accessoryListMap = Object.fromEntries(accessoryList.map(e => [e.id, e]))
          setLocalState({ ...localState, accessoryListData: accessoryList, accessoryListLoadRequired: false, loading: false, accessoryListMap, formData: { ...localState.formData, booking: { ...localState.formData.booking, bikeId: undefined, quantity: undefined, availableQuantity: undefined, rcData: undefined } } });
        } catch (e) {
          setLocalState({ ...localState, error: true, accessoryListLoadRequired: false, message: parseErrorForAxios(e) });
        }
      })();
    }
  }
  useEffect(getAvailableAccessoriesList, [localState]); // eslint-disable-line react-hooks/exhaustive-deps

  const getPriceCalculationData = async (newPaymentState: PaymentDetails) => {
    try {
      const { data } = await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders/check-price`, {
        hubId: selectedHub.id,
        bikeId: localState.formData.booking.bikeId,
        quantity: localState.formData.booking.quantity,
        accessories: localState.formData.booking.accessoryData,
        orderStart: localState.formData.booking.startDate,
        orderEnd: localState.formData.booking.endDate,
        paymentMode: newPaymentState.paymentMethod || localState.formData.payment.paymentMethod,
        vendorDiscount: newPaymentState.vendorDiscount || 0,
        delivery: false,
        ignoreTimeConstraints: Boolean(localState.formData.booking.startDate && localState.formData.booking.startDate < Date.now())
      }, {
        headers: { 'vendor-token': authCookie }
      })
      return data.data.priceData as { accessoryAmount: number, amountPayableOnline: number, bikeAmount: number, defaultDiscount: number, depositAmount: number, offlineCashAmount: number, onlineAmount: number, totalPayableAmount: number, convFees: number, goBikesCommission: number, finalVendorAmount: number };
    } catch (e) {
      throw e;
    }
  }

  // Validate customer details and compute enableNext/error
  function applyCustomerValidation(newCustomerState: CustomerDetails): CustomerDetails {
    const fieldSchema = [
      { name: 'First name', value: newCustomerState.firstName, validateFn: falsyValidationFn() },
      { name: 'Last name', value: newCustomerState.lastName, validateFn: falsyValidationFn() },
      { name: 'Country Code', value: newCustomerState.countryCode, validateFn: falsyValidationFn() },
      { name: 'Phone number', value: newCustomerState.phone, validateFn: phoneValidationFn() },
      ...(newCustomerState.email ? [{ name: 'E-mail', value: newCustomerState.email, validateFn: mailValidationFn() }] : [])
    ];
    const missingFields = parseInvalidFields(fieldSchema, { checkOnlyIfDefined: true });
    const missingFieldsForNext = parseInvalidFields(fieldSchema);
    newCustomerState.error = missingFields.length ? `Please enter missing fields: ${missingFields.join(', ')}` : '';
    newCustomerState.enableNext = !Boolean(missingFieldsForNext.length);
    return newCustomerState;
  }

  // Update customer details form content
  function updateCustomerDetails(update: Partial<CustomerDetails>) {
    let newCustomerState = { ...localState.formData.customer, ...update };

    // Clear user data and re-hide personal fields if phone changed, since any previous check is now stale
    if (update.phone) {
      newCustomerState.firstName = ''
      newCustomerState.lastName = ''
      newCustomerState.email = ''
      setPersonalDetailsState({ hidden: true, loading: false });
      setOtpState({});
    }

    newCustomerState = applyCustomerValidation(newCustomerState);
    setLocalState({
      ...localState,
      formData: {
        ...localState.formData,
        customer: newCustomerState
      }
    });
  }

  // Look up an existing user by phone (triggered by the Check Phone button)
  async function checkPhoneExists() {
    let newCustomerState = { ...localState.formData.customer };
    if (!isValidPhone(String(newCustomerState.phone))) return;
    setPersonalDetailsState({ hidden: true, loading: true });
    setOtpState({});
    try {
      const { data } = await GobikesApi.get(`/vendors/manage/users`, {
        headers: { 'vendor-token': authCookie },
        params: { phone: newCustomerState.phone, countryCode: newCustomerState.countryCode }
      });
      if (data.data.userExists) {
        newCustomerState.firstName = data.data.userData.firstName;
        newCustomerState.lastName = data.data.userData.lastName;
        newCustomerState.email = data.data.userData.email;
      }
      setOtpState({ userExists: data.data.userExists });
    } catch (e: any) {
      // Allow manual entry below even if the lookup fails
    }
    setPersonalDetailsState({ loading: false });
    newCustomerState = applyCustomerValidation(newCustomerState);
    setLocalState({
      ...localState,
      formData: {
        ...localState.formData,
        customer: newCustomerState
      }
    });
  }

  // Send OTP to verify a new customer's phone number
  async function sendPhoneOtp() {
    setOtpState({ ...otpState, loading: true, error: undefined });
    try {
      await GobikesApi.post(`/vendors/manage/users/send-otp`, {
        phone: localState.formData.customer.phone,
        countryCode: localState.formData.customer.countryCode
      }, { headers: { 'vendor-token': authCookie } });
      setOtpState({ ...otpState, loading: false, sent: true, error: undefined });
    } catch (e: any) {
      setOtpState({ ...otpState, loading: false, error: parseErrorForAxios(e) });
    }
  }

  // Verify the OTP sent to a new customer's phone number
  async function verifyPhoneOtp() {
    setOtpState({ ...otpState, loading: true, error: undefined });
    try {
      await GobikesApi.post(`/vendors/manage/users/verify-otp`, {
        phone: localState.formData.customer.phone,
        otp: otpState.value
      }, { headers: { 'vendor-token': authCookie } });
      setOtpState({ ...otpState, loading: false, verified: true, error: undefined });
    } catch (e: any) {
      setOtpState({ ...otpState, loading: false, error: parseErrorForAxios(e) });
    }
  }

  // Send OTP to verify the emergency contact number
  async function sendEmContactOtp() {
    const phone = localState.formData.booking.emContact || '';
    setEmContactOtpState({ ...emContactOtpState, phone, loading: true, error: undefined });
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/send-em-contact-otp`, { phone }, { headers: { 'vendor-token': authCookie } });
      setEmContactOtpState({ phone, loading: false, sent: true, error: undefined });
    } catch (e: any) {
      setEmContactOtpState({ phone, loading: false, error: parseErrorForAxios(e) });
    }
  }

  // Verify the OTP sent to the emergency contact number
  async function verifyEmContactOtp() {
    const phone = localState.formData.booking.emContact || '';
    setEmContactOtpState({ ...emContactOtpState, loading: true, error: undefined });
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/verify-em-contact-otp`, { phone, otp: emContactOtpState.value }, { headers: { 'vendor-token': authCookie } });
      setEmContactOtpState({ phone, sent: true, loading: false, verified: true, error: undefined });
    } catch (e: any) {
      setEmContactOtpState({ ...emContactOtpState, loading: false, error: parseErrorForAxios(e) });
    }
  }

  // Update booking details form data
  function updateBookingDetails(update: Partial<BookingDetails>, otherData?: { allRcData: OneBike[] }) {
    const newBookingState = { ...localState.formData.booking, ...update };
    const missingFields = parseInvalidFields([
      { name: 'Booking source', value: newBookingState.bookingChannel, validateFn: falsyValidationFn() },
      ...(newBookingState.bookingChannel === 'OTHER' ? [{ name: 'Booking source (specify)', value: newBookingState.bookingChannelOther, validateFn: falsyValidationFn() }] : []),
      { name: 'Start date', value: newBookingState.startDate, validateFn: falsyValidationFn() },
      { name: 'End date', value: newBookingState.endDate, validateFn: falsyValidationFn() },
      { name: 'Bike', value: newBookingState.bikeId, validateFn: falsyValidationFn() },
      { name: 'Quantity', value: newBookingState.quantity, validateFn: falsyValidationFn() },
    ]);
    let missingRcOd = false;
    for (const rc of (newBookingState.rcData || [])) {
      if (!rc.odStart || !rc.rcNumber) {
        missingRcOd = true;
        break;
      }
    }
    let missingAccData = false;
    for (const a of (newBookingState.accessoryData || [])) {
      if (!a.id || !a.quantity) {
        missingAccData = true; break;
      }
    }
    const bikeListLoadRequired = Boolean(update.startDate || update.endDate);
    if (missingRcOd) missingFields.push('Odometer/RC');
    if (missingAccData) missingFields.push('Accessory data');
    newBookingState.error = missingFields.length ? `Please enter missing fields: ${missingFields.join(', ')}` : '';
    newBookingState.enableNext = !missingRcOd && !missingFields.length;
    const newLocalState = {
      ...localState,
      bikeListLoadRequired,
      formData: {
        ...localState.formData,
        booking: newBookingState
      }
    };
    setLocalState(newLocalState);
  }

  // Update payment details form data
  async function updatePaymentDetails(update: Partial<PaymentDetails>) {
    const newState: PaymentDetails = { ...localState.formData.payment, ...update };
    if (update.deposit !== undefined) {
      newState.depositManuallySet = true;
    }
    try {
      const priceData = await getPriceCalculationData(newState);
      newState.bikeRental = priceData.bikeAmount;
      newState.accessoryRental = priceData.accessoryAmount;
      if (!newState.depositManuallySet) {
        newState.deposit = priceData.depositAmount;
      }
      newState.defaultDiscount = priceData.defaultDiscount;
      newState.finalAmountPaid = roundAndToFixed2(priceData.bikeAmount + priceData.accessoryAmount - priceData.defaultDiscount - newState.vendorDiscount);
      newState.convFees = priceData.convFees;
      newState.goBikesCommission = priceData.goBikesCommission;
      newState.finalDeductions = roundAndToFixed2(newState.convFees + newState.goBikesCommission);
      newState.finalPayableAmount = priceData.finalVendorAmount;
      newState.enableNext = true;
      setLocalState({
        ...localState,
        formData: {
          ...localState.formData,
          payment: newState
        }
      });
    } catch (e) {
      setLocalState({ ...localState, loading: true, error: true, message: parseErrorForAxios(e) });
    }
  }

  // Functions to control front back allowed
  function checkNextEnabled(): boolean {
    switch (localState.activeStep) {
      case StepIndex.CUSTOMER_DETAILS: {
        return Boolean(localState.formData.customer.enableNext) && (otpState.userExists !== false || Boolean(otpState.verified));
      }
      case StepIndex.VEHICLE_DETAILS: {
        return vehicleDetailsValid;
      }
      case StepIndex.OVERVIEW: {
        return true;
      }
      case StepIndex.BOOKING_DETAILS: {
        return bookingDetailsValid;
      }
      case StepIndex.PAYMENT_DETAILS: {
        return Boolean(localState.formData.payment.enableNext);
      }
      case StepIndex.UPLOADS: {
        return missingMandatoryUploadLabels.length === 0;
      }
      default: {
        return false;
      }
    }
  }

  // Next step
  async function handleNext(submit: any) {
    if (!submit) {
      if (localState.activeStep === StepIndex.CUSTOMER_DETAILS && localState.formData.customer.email) {
        setLocalState({ ...localState, loading: true, error: false, message: 'Validating email' });
        try {
          const { data } = await GobikesApi.get(`/vendors/manage/users/validate-email`, {
            headers: { 'vendor-token': authCookie },
            params: { email: localState.formData.customer.email, phone: localState.formData.customer.phone }
          });
          if (data.data.taken) {
            setLocalState({ ...localState, loading: false, error: true, message: `This email is already registered with another phone number (${data.data.existingPhone})` });
            return;
          }
        } catch (e) {
          setLocalState({ ...localState, loading: false, error: true, message: parseErrorForAxios(e) });
          return;
        }
        setLocalState({ ...localState, loading: false, error: false, message: '' });
      }
      if (localState.activeStep + 1 === StepIndex.PAYMENT_DETAILS) {
        const newState: PaymentDetails = { ...localState.formData.payment };
        try {
          const priceData = await getPriceCalculationData(newState);
          newState.bikeRental = priceData.bikeAmount;
          newState.accessoryRental = priceData.accessoryAmount;
          if (!newState.depositManuallySet) {
            newState.deposit = priceData.depositAmount;
          }
          newState.defaultDiscount = priceData.defaultDiscount;
          newState.finalAmountPaid = roundAndToFixed2(priceData.bikeAmount + priceData.accessoryAmount - priceData.defaultDiscount - newState.vendorDiscount);
          newState.convFees = priceData.convFees;
          newState.goBikesCommission = priceData.goBikesCommission;
          newState.finalDeductions = roundAndToFixed2(newState.convFees + newState.goBikesCommission);
          newState.finalPayableAmount = priceData.finalVendorAmount;
          newState.enableNext = true;
          setLocalState({
            ...localState,
            formData: {
              ...localState.formData,
              payment: newState
            },
            activeStep: localState.activeStep + 1
          });
        } catch (e) {
          setLocalState({ ...localState, loading: true, error: true, message: parseErrorForAxios(e) });
        }
        return;
      }
      setLocalState({ ...localState, activeStep: localState.activeStep + 1 });
      return;
    }
    const activeStep = localState.activeStep + 1;
    const images = Object.keys(localState.formData.uploads)
      .filter(label => {
        const isExtra = label === OrderUploadLabels.EXTRA_1 || label === OrderUploadLabels.EXTRA_2;
        return localState.formData.uploads[label as OrderUploadLabels] && (!isExtra || localState.formData.extraUploadNames[label as OrderUploadLabels.EXTRA_1 | OrderUploadLabels.EXTRA_2]);
      })
      .map(label => ({
        label,
        data: localState.formData.uploads[label as OrderUploadLabels] as string,
        ...(localState.formData.extraUploadNames[label as OrderUploadLabels.EXTRA_1 | OrderUploadLabels.EXTRA_2] && { customLabel: localState.formData.extraUploadNames[label as OrderUploadLabels.EXTRA_1 | OrderUploadLabels.EXTRA_2] })
      }));
    const payload = {
      firstName: localState.formData.customer.firstName,
      lastName: localState.formData.customer.lastName,
      countryCode: localState.formData.customer.countryCode,
      phone: localState.formData.customer.phone,
      email: localState.formData.customer.email,
      hubId: selectedHub.id,
      bookingChannel: localState.formData.booking.bookingChannel,
      bookingChannelOther: localState.formData.booking.bookingChannelOther,
      bikeId: localState.formData.booking.bikeId,
      quantity: localState.formData.booking.quantity,
      orderStart: localState.formData.booking.startDate,
      orderEnd: localState.formData.booking.endDate,
      bikeData: localState.formData.booking.rcData,
      accessoryIds: (localState.formData.booking.accessoryData || []).map(a => a.id),
      accessoryQty: (localState.formData.booking.accessoryData || []).map(a => a.quantity),
      paymentMode: localState.formData.payment.paymentMethod,
      bikeAmount: localState.formData.payment.bikeRental,
      accessoryAmount: localState.formData.payment.accessoryRental,
      vendorDiscount: localState.formData.payment.vendorDiscount,
      deposit: localState.formData.payment.deposit,
      ignoreTimeConstraints: Boolean(localState.formData.booking.startDate && localState.formData.booking.startDate < Date.now()),
      emContact: localState.formData.booking.emContact,
      localAddress: localState.formData.booking.localAddress,
      ...(localState.formData.booking.bookingType === BookingType.SUBSCRIPTION && {
        bookingType: localState.formData.booking.bookingType,
        billingCycle: localState.formData.booking.billingCycle
      }),
      ...(images.length && { images })
    };
    setLocalState({ ...localState, loading: true, message: 'Creating booking', error: false, activeStep });
    try {
      await GobikesApi.post(`/vendors/hubs/${selectedHub.id}/orders`, payload, { headers: { 'vendor-token': authCookie } });
      setLocalState({ ...localState, loading: false, error: false, message: 'Booking created', activeStep });
      setTimeout(() => { props.backFn(true) }, 2000);
    } catch (e: any) {
      setLocalState({ ...localState, loading: false, error: true, message: parseErrorForAxios(e), activeStep: localState.activeStep });
    }
  }

  // Previous step
  function handleBack() {
    setLocalState({ ...localState, activeStep: localState.activeStep - 1 })
  }

  // Reset form
  function handleReset() {
    setLocalState({ ...localState, activeStep: 0, formData: { customer: {}, booking: {}, payment: { bikeRental: 0, accessoryRental: 0, defaultDiscount: 0, vendorDiscount: 0, deposit: 0 }, uploads: {}, extraUploadNames: {}, extraUploadCount: 0 } })
  }

  // Render content
  function renderContent() {
    return (
      <Fragment>
        <div className="gbk-paper-container create-booking">

          {/* Empty space at top for phone screen */}
          <Fragment>
            <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '18px', sm: '26px' }, width: 1 }}><br /></Box>
          </Fragment>

          <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
            {/* Stepper */}
            {isPhone ? (
              <div style={{ display: 'flex', alignItems: 'flex-start', padding: '12px 16px 8px', borderBottom: '1px solid #eee' }}>
                {steps.map((label, i) => (
                  <Fragment key={i}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        backgroundColor: localState.activeStep === i ? '#558b2f' : localState.activeStep > i ? '#8bc34a' : '#e0e0e0',
                        color: localState.activeStep >= i ? 'white' : '#999',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', fontWeight: 'bold', flexShrink: 0
                      }}>
                        {i + 1}
                      </div>
                      <span style={{ fontSize: '10px', marginTop: 2, color: localState.activeStep === i ? '#558b2f' : '#999', textAlign: 'center', maxWidth: 52, lineHeight: '12px' }}>
                        {label}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div style={{ flex: 1, height: 2, backgroundColor: localState.activeStep > i ? '#8bc34a' : '#e0e0e0', margin: '0 4px', marginTop: 12 }} />
                    )}
                  </Fragment>
                ))}
              </div>
            ) : (
              <Box className="header" sx={{
                paddingLeft: { md: '24px !important', lg: '60px !important', xl: '200px !important' },
                paddingRight: { md: '24px !important', lg: '60px !important', xl: '200px !important' }
              }}>
                <Stepper className="clean-stepper" activeStep={localState.activeStep}>
                  {steps.map((label) => (
                    <Step key={label}><StepLabel>{label}</StepLabel></Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {/* Content */}
            <div className="content create-booking-form">
              {renderForm()}
            </div>
          </Paper>
        </div>

        {/* Action buttons */}
        <Box className="create-booking-actions" sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: props.drawerWidth },
          width: { xs: '100%', md: `calc(100vw - ${props.drawerWidth}px)` },
          padding: { xs: '10px 16px', md: '20px 40px' },
          paddingBottom: { xs: 'calc(10px + env(safe-area-inset-bottom, 0px))', md: 'calc(20px + env(safe-area-inset-bottom, 0px))' },
          background: '#fff',
          boxShadow: '0px -2px 6px #ddd',
          display: 'flex',
          gap: { xs: '10px', md: 0 },
          boxSizing: 'border-box',
        }}>
          {localState.activeStep === steps.length ? (
            <Button onClick={handleReset}>Done</Button>
          ) : (
            <Fragment>
              <Button
                color="inherit"
                disabled={localState.activeStep === 0}
                onClick={handleBack}
                variant={isPhone ? 'outlined' : 'text'}
                sx={{ flexShrink: 0, mr: { xs: 0, md: 1 }, minWidth: { xs: '80px', md: 'auto' }, ...(isPhone && { textTransform: 'none', fontWeight: 600 }) }}
              >
                Back
              </Button>
              <Button
                fullWidth={isPhone}
                onClick={() => handleNext(localState.activeStep === steps.length - 1)}
                disabled={!checkNextEnabled()}
                variant='contained'
                sx={{ ...(isPhone && { textTransform: 'none', fontWeight: 700, fontSize: '14px' }) }}
              >
                {localState.activeStep === steps.length - 1 ? 'Create Order' : 'Next'}
              </Button>
            </Fragment>
          )}
        </Box>
      </Fragment >
    )
  }

  // Render input form
  function renderForm() {

    if (localState.error) {
      return (
        <DashboardLoader
          {...localState}
          styles={{
            containerSx: {
              mt: { xs: 0, md: '40px' },
              width: 'calc(100%)',
              height: 'auto',
              paddingTop: '200px',
              paddingBottom: '200px',
              boxShadow: 'none'
            },
          }}
          customIcon={<DoneAll className="icon" style={{ color: Colors.primary }} />}
          resetError={localState.error ? () => setLocalState({ ...localState, error: false, loading: false, activeStep: StepIndex.PAYMENT_DETAILS }) : undefined}
          resetErrorText={'Dismiss'}
          resetErrorIcon={<Close />}
          resetErrorIconPrefix={true}
        />
      )
    }

    switch (localState.activeStep) {
      case StepIndex.CUSTOMER_DETAILS: {
        return renderCustomerDetails();
      }
      case StepIndex.VEHICLE_DETAILS: {
        return renderVehicleDetails();
      }
      case StepIndex.OVERVIEW: {
        return renderOverview();
      }
      case StepIndex.BOOKING_DETAILS: {
        return renderBookingDetails();
      }
      case StepIndex.PAYMENT_DETAILS: {
        return renderPaymentDetails();
      }
      case StepIndex.UPLOADS: {
        return renderUploads();
      }
      default: {
        return renderOrderRequest();
      }
    }
  }

  // Render uploads (optional pickup photos, matching the Start Pickup Process uploads step -
  // vendor bookings auto-start the ride on creation, so this is the only chance to capture them)
  function renderUploads() {
    const uploadFieldSx: SxProps = {
      width: { xs: '100%', md: '200px' },
      marginBottom: { xs: '8px', md: '20px' }
    };
    function updateUpload(label: OrderUploadLabels, v: string) {
      setLocalState({ ...localState, formData: { ...localState.formData, uploads: { ...localState.formData.uploads, [label]: v } } });
    }
    return (
      <Fragment>
        {isPhone ? <Box sx={{ height: '8px' }} /> : <br />}
        <Box sx={{ margin: { xs: '0 16px', md: '0 30px' }, width: { xs: '92%', md: '80%' } }}>
          <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Uploads</b> <span style={{ fontWeight: 400, color: '#999', fontSize: '13px' }}>{requiresMandatoryPickupPhotos ? '(Required)' : '(Optional)'}</span></Typography>
          <Box sx={{ display: { xs: 'grid', md: 'block' }, gridTemplateColumns: '1fr 1fr', gap: '8px', mt: 1 }}>
            <GobikesField label={`Customer Photo${missingMandatoryUploadLabels.includes(OrderUploadLabels.CUSTOMER) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.CUSTOMER} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.CUSTOMER || ''} updateFn={v => updateUpload(OrderUploadLabels.CUSTOMER, v)} />
            <GobikesField label={`Vehicle Left${missingMandatoryUploadLabels.includes(OrderUploadLabels.BIKE_LEFT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.BIKE_LEFT} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.BIKE_LEFT || ''} updateFn={v => updateUpload(OrderUploadLabels.BIKE_LEFT, v)} />
            <GobikesField label={`Vehicle Right${missingMandatoryUploadLabels.includes(OrderUploadLabels.BIKE_RIGHT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.BIKE_RIGHT} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.BIKE_RIGHT || ''} updateFn={v => updateUpload(OrderUploadLabels.BIKE_RIGHT, v)} />
            <GobikesField label={`Vehicle Front${missingMandatoryUploadLabels.includes(OrderUploadLabels.BIKE_FRONT) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.BIKE_FRONT} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.BIKE_FRONT || ''} updateFn={v => updateUpload(OrderUploadLabels.BIKE_FRONT, v)} />
            <GobikesField label={`Vehicle Back${missingMandatoryUploadLabels.includes(OrderUploadLabels.BIKE_BACK) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.BIKE_BACK} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.BIKE_BACK || ''} updateFn={v => updateUpload(OrderUploadLabels.BIKE_BACK, v)} />
            <GobikesField label={`Odometer${missingMandatoryUploadLabels.includes(OrderUploadLabels.ODOMETER) ? ' *' : ''}`} type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.ODOMETER} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.ODOMETER || ''} updateFn={v => updateUpload(OrderUploadLabels.ODOMETER, v)} />
            <GobikesField label="Other" type={GbkFieldType.UPLOAD} tag={OrderUploadLabels.OTHER} gbkFieldSx={uploadFieldSx} editMode={true} value={localState.formData.uploads.OTHER || ''} updateFn={v => updateUpload(OrderUploadLabels.OTHER, v)} />
            {([OrderUploadLabels.EXTRA_1, OrderUploadLabels.EXTRA_2] as const).slice(0, localState.formData.extraUploadCount).map(label => (
              <Box key={label} sx={{ ...uploadFieldSx, display: { xs: 'flex', md: 'inline-flex' }, flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'flex-start', md: 'stretch' }, gap: '8px', gridColumn: { xs: '1 / -1', md: 'auto' }, verticalAlign: 'top', width: { xs: '100%', md: '200px' }, mr: { md: '20px' } }}>
                <GobikesField
                  label="Upload Name"
                  type={GbkFieldType.STRING}
                  gbkFieldSx={{ flex: 1, width: '100% !important' }}
                  editMode={true}
                  useInternalLabel={true}
                  value={localState.formData.extraUploadNames[label] || ''}
                  updateFn={(v: string) => setLocalState({ ...localState, formData: { ...localState.formData, extraUploadNames: { ...localState.formData.extraUploadNames, [label]: v } } })}
                />
                <GobikesField label="Upload" type={GbkFieldType.UPLOAD} tag={label} gbkFieldSx={{ flexShrink: 0 }} editMode={true} value={localState.formData.uploads[label] || ''} updateFn={v => updateUpload(label, v)} />
              </Box>
            ))}
          </Box>
          {localState.formData.extraUploadCount < 2 && (
            <Button
              variant="outlined"
              className="no-text-transform"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setLocalState({ ...localState, formData: { ...localState.formData, extraUploadCount: localState.formData.extraUploadCount + 1 } })}
            >
              + Add Extra Upload
            </Button>
          )}
        </Box>
      </Fragment>
    );
  }

  // Render customer details
  function renderCustomerDetails() {
    const gap = <Box sx={{ height: isPhone ? '4px' : '16px' }} />;
    return (
      <Fragment>
        {isPhone ? <Box sx={{ height: '8px' }} /> : <br />}
        <div className="gbk-col" style={{ minWidth: isPhone ? 0 : 190 }}></div>
        <div className="gbk-col" style={{ minWidth: isPhone ? 0 : 400, width: isPhone ? '100%' : undefined, maxWidth: isPhone ? '100%' : undefined }}>
          <GobikesField
            type={GbkFieldType.SELECT}
            label={<span>Country Code <span className="error-text ">*</span></span>}
            editMode={true}
            value={localState.formData.customer.countryCode || ''}
            updateFn={(v: string) => {
              updateCustomerDetails({ countryCode: v });
            }}
            validateFn={phoneValidationFn()}
            selectOptions={CountryCodesList}
            gbkFieldSx={{ width: { xs: '100%', md: 300 } }}
          />
          {gap}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: '8px', maxWidth: { xs: '100%', md: 400 } }}>
            <Box sx={{ flex: 1 }}>
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={<span>Phone Number <span className="error-text ">*</span></span>}
                editMode={true}
                value={localState.formData.customer.phone || ''}
                updateFn={(v: number) => {
                  updateCustomerDetails({ phone: v });
                }}
                validateFn={phoneValidationFn()}
              />
            </Box>
            {isValidPhone(String(localState.formData.customer.phone || '')) ? (
              otpState.userExists !== false ? (
                <Box sx={{ flexShrink: 0, mb: { xs: 0, sm: '23px' } }}>
                  <Button
                    variant='outlined'
                    size='small'
                    disabled={personalDetailsState.loading}
                    onClick={checkPhoneExists}
                    sx={{ textTransform: 'none' }}
                  >
                    {personalDetailsState.loading ? 'Checking...' : 'Check Phone'}
                  </Button>
                </Box>
              ) : otpState.verified ? (
                <Box sx={{ flexShrink: 0, mb: { xs: 0, sm: '23px' } }}>
                  <Typography sx={{ fontSize: '14px', color: Colors.primary }}>Phone Verified</Typography>
                </Box>
              ) : !otpState.sent ? (
                <Box sx={{ flexShrink: 0, mb: { xs: 0, sm: '23px' } }}>
                  <Button
                    variant='outlined'
                    size='small'
                    disabled={otpState.loading}
                    onClick={sendPhoneOtp}
                    sx={{ textTransform: 'none' }}
                  >
                    {otpState.loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                </Box>
              ) : ''
            ) : ''}
          </Box>
          {otpState.userExists === false && otpState.sent && !otpState.verified ? (
            <Fragment>
              {gap}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-end' }, gap: '8px', maxWidth: { xs: '100%', md: 400 } }}>
                <Box sx={{ flex: 1 }}>
                  <GobikesField
                    type={GbkFieldType.STRING}
                    label="Enter OTP"
                    editMode={true}
                    value={otpState.value || ''}
                    updateFn={(v: string) => setOtpState({ ...otpState, value: v })}
                  />
                </Box>
                <Box sx={{ flexShrink: 0, mb: { xs: 0, sm: '23px' }, display: 'flex', gap: '8px' }}>
                  <Button variant='outlined' size='small' disabled={otpState.loading || !otpState.value} onClick={verifyPhoneOtp} sx={{ textTransform: 'none' }}>
                    {otpState.loading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  <Button variant='text' size='small' disabled={otpState.loading} onClick={sendPhoneOtp} sx={{ textTransform: 'none' }}>
                    Resend
                  </Button>
                </Box>
              </Box>
            </Fragment>
          ) : ''}
          {otpState.userExists === false && otpState.error ? (
            <Typography className="error-text" sx={{ fontSize: '13px', mt: 0.5 }}>{otpState.error}</Typography>
          ) : ''}
          <span hidden={personalDetailsState.hidden}>
            {gap}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '16px', width: { xs: '100%', md: 700 } }}>
              <Box sx={{ flex: 1 }}>
                <GobikesField
                  type={GbkFieldType.STRING}
                  label={<span>First Name <span className="error-text ">*</span></span>}
                  editMode={true}
                  value={localState.formData.customer.firstName || ''}
                  updateFn={(v: string) => {
                    updateCustomerDetails({ firstName: v });
                  }}
                  validateFn={falsyValidationFn()}
                  gbkFieldSx={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <GobikesField
                  type={GbkFieldType.STRING}
                  label={<span>Last Name <span className="error-text ">*</span></span>}
                  editMode={true}
                  value={localState.formData.customer.lastName || ''}
                  updateFn={(v: string) => {
                    updateCustomerDetails({ lastName: v });
                  }}
                  validateFn={falsyValidationFn()}
                  gbkFieldSx={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <GobikesField
                  type={GbkFieldType.STRING}
                  label={'E-mail'}
                  editMode={true}
                  value={localState.formData.customer.email || ''}
                  updateFn={(v: string) => {
                    updateCustomerDetails({ email: v });
                  }}
                  validateFn={(s: string) => {
                    if (!s) return '';
                    return mailValidationFn()(s);
                  }}
                  gbkFieldSx={{ width: '100%' }}
                />
              </Box>
            </Box>
          </span>
        </div>
        {isPhone ? <Box sx={{ height: '16px' }} /> : <><br /><br /><br /></>}
      </Fragment>
    )
  }

  // Render booking source + specify-source fields
  function renderBookingSourceFields(styles: { [k: string]: SxProps<Theme> }) {
    return (
      <Fragment>
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col" sx={styles.col}>
          <GobikesField
            type={GbkFieldType.SELECT}
            label={<span>Booking Source <span className="error-text ">*</span></span>}
            editMode={true}
            selectOptions={bookingChannelOptions}
            value={localState.formData.booking.bookingChannel || ''}
            updateFn={(v: string) => {
              updateBookingDetails({ bookingChannel: v, ...(v !== 'OTHER' && { bookingChannelOther: undefined }) });
            }}
          />
        </Box>
        {localState.formData.booking.bookingChannel === 'OTHER' ? (
          <Box className="gbk-col" sx={styles.col}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={<span>Specify Source <span className="error-text ">*</span></span>}
              editMode={true}
              value={localState.formData.booking.bookingChannelOther || ''}
              updateFn={(v: string) => {
                updateBookingDetails({ bookingChannelOther: v });
              }}
            />
          </Box>
        ) : ''}
        {isPhone ? <Box sx={{ height: '4px' }} /> : <br />}
      </Fragment>
    );
  }

  // Render pickup/dropoff dates + bike/quantity + per-unit RC/odometer fields
  function renderVehicleDetailsFields(styles: { [k: string]: SxProps<Theme> }) {
    const isSubscription = localState.formData.booking.bookingType === BookingType.SUBSCRIPTION;
    return (
      <Fragment>
        {profile.subscriptionEnabled ? (
          <Fragment>
            <Box className="gbk-col" sx={styles.spacer}></Box>
            <Box className="gbk-col field-col no-pad-bot" sx={{ ...styles.col, width: { xs: '100%', lg: 'auto' } }}>
              <Typography sx={isPhone ? { fontSize: '13px', fontWeight: 700 } : undefined} variant={isPhone ? undefined : 'h6'} className={isPhone ? undefined : 'bold'}>
                Booking Type <span className="error-text ">*</span>
              </Typography>
              <RadioGroup
                row
                value={localState.formData.booking.bookingType || BookingType.STANDARD}
                onChange={(e) => {
                  const v = e.target.value as BookingType;
                  const isNewSubscription = v === BookingType.SUBSCRIPTION;
                  const endDate = isNewSubscription && localState.formData.booking.startDate && localState.formData.booking.billingCycle
                    ? computeBillingCycleEnd(localState.formData.booking.startDate, localState.formData.booking.billingCycle)
                    : undefined;
                  updateBookingDetails({
                    bookingType: v,
                    ...(isNewSubscription ? { billingCycle: localState.formData.booking.billingCycle, endDate } : { billingCycle: undefined })
                  });
                }}
              >
                {bookingTypeOptions.map(o => (
                  <FormControlLabel key={o.value} value={o.value} control={<Radio size="small" sx={{ color: Colors.primary, '&.Mui-checked': { color: Colors.primary } }} />} label={o.display} />
                ))}
              </RadioGroup>
            </Box>
            {!isPhone && <br />}
          </Fragment>
        ) : ''}
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col" sx={{ ...styles.col, width: { xs: '100%', lg: 320 } }}>
          <GobikesField
            type={GbkFieldType.DATE_TIME}
            label={<span>Pickup Date and Time <span className="error-text ">*</span></span>}
            editMode={true}
            value={localState.formData.booking.startDate ? DateTime.fromMillis(localState.formData.booking.startDate) : null}
            updateFn={(v: number) => {
              const startDate = DateTime.fromMillis(v).set({ second: 0, millisecond: 0 }).toMillis();
              const endDate = isSubscription && localState.formData.booking.billingCycle
                ? computeBillingCycleEnd(startDate, localState.formData.booking.billingCycle)
                : undefined;
              updateBookingDetails({ startDate, ...(isSubscription && { endDate }) });
            }}
          />
        </Box>
        {isSubscription ? (
          <Box className="gbk-col" sx={{ ...styles.col, width: { xs: '100%', lg: 320 } }}>
            <GobikesField
              type={GbkFieldType.SELECT}
              label={<span>Billing Cycle <span className="error-text ">*</span></span>}
              editMode={true}
              selectOptions={billingCycleOptions}
              value={localState.formData.booking.billingCycle || ''}
              updateFn={(v: BillingCycle) => {
                const endDate = localState.formData.booking.startDate ? computeBillingCycleEnd(localState.formData.booking.startDate, v) : undefined;
                updateBookingDetails({ billingCycle: v, endDate });
              }}
            />
            {localState.formData.booking.endDate ? (
              <Typography sx={{ fontSize: '12px', color: '#666', mt: '4px' }}>
                Next billing date: {DateTime.fromMillis(localState.formData.booking.endDate).toFormat('MMM d, h:mm a')}
              </Typography>
            ) : ''}
          </Box>
        ) : (
          <Box className="gbk-col" sx={{ ...styles.col, width: { xs: '100%', lg: 320 } }}>
            <GobikesField
              type={GbkFieldType.DATE_TIME}
              label={<span>Drop off Date and Time <span className="error-text ">*</span></span>}
              editMode={true}
              value={localState.formData.booking.endDate ? DateTime.fromMillis(localState.formData.booking.endDate) : null}
              minDateTime={localState.formData.booking.startDate ? DateTime.fromMillis(localState.formData.booking.startDate).plus({ hours: 1 }) : DateTime.now().plus({ hours: 1 }).set({ minute: 0 })}
              updateFn={(v: number) => {
                updateBookingDetails({ endDate: DateTime.fromMillis(v).set({ second: 0, millisecond: 0 }).toMillis() });
              }}
            />
          </Box>
        )}
        {!isPhone && <br />}
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col field-col no-pad-bot" sx={styles.col}>
          {isPhone && <Box sx={{ height: '4px' }} />}
          <Box className="gbk-col" sx={styles.col}>
            <Typography variant="h6" className="bold">Vehicle Details</Typography>
          </Box>
        </Box>
        {!isPhone && <br />}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '20px',
          pl: { xs: 0, sm: '26px', lg: '100px', xl: '190px' }
        }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Box sx={styles.col}>
            <GobikesField
              type={GbkFieldType.SELECT}
              label={<span>Select Vehicle <span className="error-text ">*</span></span>}
              editMode={true}
              selectOptions={(localState.bikeListData || []).map(b => ({ value: b.id, display: `${b.brand} ${b.name}` }))}
              value={localState.formData.booking.bikeId || ''}
              updateFn={(v) => {
                updateBookingDetails({
                  bikeId: v,
                  availableQuantity: v ? (
                    (localState.bikeListData || []).find(a => a.id.toString() === v.toString())?.hubs?.[0]?.hub_bikes?.dynamicQuantity
                  ) : undefined
                });
              }}
            />
          </Box>
          <Box sx={styles.col}>
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={<span>{`Quantity ${localState.formData.booking.availableQuantity ? `(Available: ${localState.formData.booking.availableQuantity})` : ''}`} <span className="error-text ">*</span></span>}
              editMode={true}
              value={localState.formData.booking.quantity || ''}
              validateFn={betweenValuesValidationFn(1, localState.formData.booking.availableQuantity || 0, `Between 1 and ${localState.formData.booking.availableQuantity || 0} is allowed`)}
              updateFn={(v: number) => {
                const quantity = localState.formData.booking.quantity || 0;
                const newQuantity = Math.min(v, localState.formData.booking.availableQuantity || 0)
                let newRcData = localState.formData.booking.rcData || [];
                if (newQuantity > quantity) {
                  for (let i = 0; i < newQuantity - quantity; i++) {
                    newRcData.push({});
                  }
                } else if (newQuantity < quantity) {
                  const rcToRemove = new Set();
                  for (let i = newQuantity - 1; i < quantity; i++) {
                    if (newRcData?.[i]?.rcNumber) rcToRemove.add(newRcData[i].rcNumber);
                  }
                  if (rcToRemove.size) {
                    const allRcData = localState?.bikeListMap?.[localState.formData.booking.bikeId || 0]?.hubs?.[0]?.hub_bikes?.bikes || [];
                    allRcData?.forEach(d => {
                      if (rcToRemove.has(d.rcNumber)) d.used = false;
                    })// This is being set as new one in update function below
                  }
                  newRcData = newRcData?.slice(0, newQuantity);
                }
                updateBookingDetails({ quantity: Number(v), rcData: newRcData });
              }}
            />
          </Box>
          </Box>
          {
            localState.formData.booking.rcData?.map((rcData, i) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }} key={i}>
                <Box sx={styles.col}>
                  <GobikesField
                    type={GbkFieldType.SELECT}
                    label={<span>{`Select Vehicle Number ${i + 1}`} <span className="error-text ">*</span></span>}
                    selectOptions={validRcSelectOptions}
                    editMode={true}
                    value={rcData.rcNumber || ''}
                    updateFn={(v: string) => {
                      const allRcData = localState?.bikeListMap?.[localState.formData.booking.bikeId || 0]?.hubs?.[0]?.hub_bikes?.bikes || [];
                      allRcData.map(d => {
                        if (d.rcNumber === rcData.rcNumber) d.used = false;
                        if (d.rcNumber === v) d.used = true;
                        return d;
                      }); // This is being set as new one in update function below
                      const newRcData = localState.formData.booking.rcData || [];
                      newRcData[i].rcNumber = v;
                      updateBookingDetails({ rcData: newRcData });
                    }}
                  />
                </Box>
                <Box sx={styles.col}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={<span>{`Odometer Start Reading ${i + 1}`} <span className="error-text ">*</span></span>}
                    editMode={true}
                    value={rcData.odStart || ''}
                    updateFn={(v: number) => {
                      const newRcData = localState.formData.booking.rcData || [];
                      newRcData[i].odStart = v;
                      updateBookingDetails({ rcData: newRcData });
                    }}
                  />
                </Box>
              </Box>
            ))
          }
        </Box>
      </Fragment>
    );
  }

  // Render accessory selection fields
  function renderAccessoryFields(styles: { [k: string]: SxProps<Theme> }) {
    return (
      <Fragment>
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col field-col no-pad-bot" sx={styles.col}>
          {isPhone ? <Box sx={{ height: '4px' }} /> : <br />}
          <Box className="gbk-col" sx={styles.col}>
            <Typography sx={isPhone ? { fontSize: '13px', fontWeight: 700 } : undefined} variant={isPhone ? undefined : 'h6'} className={isPhone ? undefined : 'bold'}>Accessory Details</Typography>
          </Box>
        </Box>
        {isPhone ? <Box sx={{ height: '2px' }} /> : <br />}
        {
          localState.formData.booking.accessoryData?.map((accessoryData, i) => (
            <Fragment key={i}>
              <Box className="gbk-col" sx={styles.spacer}></Box>
              <Box className="gbk-col" sx={styles.col}>
                <GobikesField
                  type={GbkFieldType.SELECT}
                  label={`Select Accessory ${i + 1}`}
                  selectOptions={validAccessoryOptions}
                  editMode={true}
                  value={accessoryData.id || ''}
                  updateFn={(v: number) => {
                    const newAccessoryData = localState.formData.booking.accessoryData || [];
                    newAccessoryData[i].id = v;
                    updateBookingDetails({ accessoryData: newAccessoryData });
                  }}
                />
              </Box>
              <Box className="gbk-col" sx={styles.col}>
                <GobikesField
                  type={GbkFieldType.NUMBER}
                  label={`Quantity ${localState.formData.booking.quantity && accessoryData.id && localState?.accessoryListMap?.[accessoryData.id] ? `(Max: ${localState.accessoryListMap[accessoryData.id].maxQuantityPerBike * localState.formData.booking.quantity})` : ''}`}
                  editMode={true}
                  value={accessoryData.quantity}
                  updateFn={(v: number) => {
                    const newAccessoryData = localState.formData.booking.accessoryData || [];
                    newAccessoryData[i].quantity = v;
                    updateBookingDetails({ accessoryData: newAccessoryData });
                  }}
                />
                <span
                  className="gbk-icon-span clickable"
                  style={{ color: Colors.failureRed, verticalAlign: 'bottom', lineHeight: '54px', marginLeft: '4px' }}
                  onClick={() => {
                    const newAccessoryData = (localState.formData.booking.accessoryData || []);
                    newAccessoryData.splice(i, 1);
                    updateBookingDetails({ accessoryData: newAccessoryData });
                  }}
                >
                  <Cancel />
                </span>
              </Box>
              <br />
            </Fragment>
          ))
        }
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col field-col no-pad-bot" sx={styles.col}>
          <Button
            disabled={!canAddMoreAccessories}
            variant='outlined'
            onClick={() => {
              const newAccessoryData = localState.formData.booking.accessoryData || [];
              newAccessoryData.push({ id: 0, quantity: 0 });
              updateBookingDetails({ accessoryData: newAccessoryData });
            }}
          >
            {canAddMoreAccessories ? 'Add Accessory' : 'No remaining accessories'}
          </Button>
        </Box>
      </Fragment>
    );
  }

  // Render emergency contact + local address fields (no Pickup OTP - vendor and customer are both present)
  function renderEmergencyContactFields(styles: { [k: string]: SxProps<Theme> }) {
    return (
      <Fragment>
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col field-col no-pad-bot" sx={styles.col}>
          {isPhone ? <Box sx={{ height: '4px' }} /> : <br />}
          <Box className="gbk-col" sx={styles.col}>
            <Typography sx={isPhone ? { fontSize: '13px', fontWeight: 700 } : undefined} variant={isPhone ? undefined : 'h6'} className={isPhone ? undefined : 'bold'}>Other Details</Typography>
          </Box>
        </Box>
        {isPhone ? <Box sx={{ height: '2px' }} /> : <br />}
        <Box className="gbk-col" sx={styles.spacer}></Box>
        <Box className="gbk-col" sx={styles.col}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Emergency Contact' + (profile.mandatoryEmContact ? ' *' : '')}
            editMode={true}
            value={localState.formData.booking.emContact || ''}
            validateFn={phoneValidationFn(!profile.mandatoryEmContact)}
            updateFn={(v: string) => {
              updateBookingDetails({ emContact: String(v || '') });
            }}
          />
          {isValidPhone(String(localState.formData.booking.emContact || '')) ? (
            emContactVerified ? (
              <Typography sx={{ fontSize: '14px', color: Colors.primary, mt: 1 }}>Verified</Typography>
            ) : !emContactOtpState.sent || emContactOtpState.phone !== localState.formData.booking.emContact ? (
              <Box sx={{ mt: 1 }}>
                <Button variant='outlined' size='small' disabled={emContactOtpState.loading} onClick={sendEmContactOtp} sx={{ textTransform: 'none' }}>
                  {emContactOtpState.loading ? 'Sending...' : 'Send OTP'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', mt: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <GobikesField
                    type={GbkFieldType.STRING}
                    label="Enter OTP"
                    editMode={true}
                    value={emContactOtpState.value || ''}
                    updateFn={(v: string) => setEmContactOtpState({ ...emContactOtpState, value: v })}
                  />
                </Box>
                <Box sx={{ flexShrink: 0, mb: '23px', display: 'flex', gap: '8px' }}>
                  <Button variant='outlined' size='small' disabled={emContactOtpState.loading || !emContactOtpState.value} onClick={verifyEmContactOtp} sx={{ textTransform: 'none' }}>
                    {emContactOtpState.loading ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                  <Button variant='text' size='small' disabled={emContactOtpState.loading} onClick={sendEmContactOtp} sx={{ textTransform: 'none' }}>
                    Resend
                  </Button>
                </Box>
              </Box>
            )
          ) : ''}
          {emContactOtpState.error && (
            <Typography className="error-text" sx={{ fontSize: '13px', mt: 0.5 }}>{emContactOtpState.error}</Typography>
          )}
        </Box>
        <Box className="gbk-col" sx={styles.col}>
          <GobikesField
            type={GbkFieldType.STRING}
            label={'Local Address'}
            editMode={true}
            value={localState.formData.booking.localAddress || ''}
            updateFn={(v: string) => {
              updateBookingDetails({ localAddress: v });
            }}
          />
        </Box>
        <Box sx={{ height: { xs: '4px', md: '12px' } }} />
      </Fragment>
    );
  }

  // Render booking details: source + accessories + emergency contact/local address.
  // Dates/bike/RC are collected separately on the Vehicle Details step.
  function renderBookingDetails() {
    const styles: { [k: string]: SxProps<Theme> } = {
      spacer: { minWidth: { xs: 0, sm: 26, lg: 190 } },
      col: { width: { xs: '100%', lg: 280 } }
    }
    return (
      <Fragment>
        {isPhone ? <Box sx={{ height: '8px' }} /> : <br />}
        {renderBookingSourceFields(styles)}
        {renderAccessoryFields(styles)}
        {!isPhone && <br />}
        {renderEmergencyContactFields(styles)}
        {isPhone ? <Box sx={{ height: '16px' }} /> : <><br /><br /><br /><br /><br /></>}
      </Fragment>
    )
  }

  // Render vehicle details (immediate-pickup flow only)
  function renderVehicleDetails() {
    const styles: { [k: string]: SxProps<Theme> } = {
      spacer: { minWidth: { xs: 0, sm: 26, lg: 100, xl: 190 } },
      col: { width: { xs: '100%', lg: 190, xl: 280 } }
    }
    return (
      <Fragment>
        <br />
        {renderVehicleDetailsFields(styles)}
        <br />
        <br />
        <br />
      </Fragment>
    )
  }

  // Render overview (immediate-pickup flow only): pickup checklist + selected bike's charges/limits
  function renderOverview() {
    const checkboxOptions = [
      'Got the documentation done',
      'Provided helmet to the customer',
      'Provided vehicle documents to the customer',
      'Took video of the vehicle',
      'Asked customer to take a video of the vehicle',
      'Clicked photo of the customer with the vehicle'
    ];
    return (
      <Fragment>
        <br />
        <Box className="gbk-col" style={{ minWidth: 190 }}></Box>
        <Box className="gbk-col" style={{ minWidth: 400 }}>
          <Typography sx={{ fontSize: { xs: '13px', md: '16px' }, paddingBottom: { xs: '4px', md: '6px' } }}><b>Pickup Process</b></Typography>
          <ul className="roboto gbk-list" style={{ fontSize: isPhone ? '13px' : undefined, margin: isPhone ? '4px 0' : undefined }}>
            {checkboxOptions.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <br />
          <Typography sx={{ fontSize: { xs: '13px', md: '16px' } }}><b>Charges and Limits</b></Typography>
          <ul className="roboto gbk-list" style={{ marginTop: '8px', fontSize: isPhone ? '13px' : undefined }}>
            <li>
              Excess charges:
              {selectedHubBike?.penalty?.distance ? <span> <span className="roboto">₹</span>{selectedHubBike.penalty.distance} / Km</span> : ' Not configured'}
            </li>
            <li>
              Late Penalty:
              {selectedHubBike?.penalty?.late ? <span> <span className="roboto">₹</span>{selectedHubBike.penalty.late} / Hour</span> : ' No penalty'}
            </li>
          </ul>
        </Box>
        <br /><br /><br />
      </Fragment>
    );
  }

  // Payment details
  function renderPaymentDetails() {
    const styles: { [k: string]: SxProps<Theme> } = {
      spacer: { minWidth: { xs: 0, lg: 190 } },
      col: { width: { xs: '100%', lg: 'calc(100% - 190px)' } },
      field: { width: { xs: '100%', lg: '500px' } },
      value: { width: '100%' }
    }

    // Hide fields that are not required
    const isAccessoryPriceRequired = Boolean(localState.formData.payment.accessoryRental);
    const isDefaultDiscountRequired = Boolean(localState.formData.payment.defaultDiscount);
    const isVendorDiscountRequired = true; // Boolean(localState.formData.payment.vendorDiscount);

    // Calculate expression for final amount paid by customer calculation
    const firstPart = `(A${isAccessoryPriceRequired ? '+ B' : ''})`;
    const secondPartElements = [];
    if (isDefaultDiscountRequired) secondPartElements.push('C');
    if (isVendorDiscountRequired) secondPartElements.push('D');
    const calculationExpression = `${firstPart}${secondPartElements.length ? ` - (${secondPartElements.join(' + ')})` : ''}`;

    // Phone card layout
    if (isPhone) {
      const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', padding: '11px 14px', borderTop: '1px solid #f2f2f2' };
      const labelStyle: CSSProperties = { flex: 1, fontSize: 13, color: '#666' };
      const valueStyle: CSSProperties = { fontSize: 14, fontWeight: 700, color: '#222' };
      const sectionHeaderStyle: CSSProperties = { padding: '10px 14px 6px', fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px' };
      const cardStyle: CSSProperties = { background: '#fff', borderRadius: 14, marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' };
      const inputRowStyle: CSSProperties = { padding: '12px 14px 6px', borderTop: '1px solid #f2f2f2' };
      const inputLabelStyle: CSSProperties = { fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 };
      return (
        <div className="cb-payment-mobile" style={{ padding: '8px 12px 16px' }}>
          {/* Charges card */}
          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Payment Details</div>
            <div style={rowStyle}>
              <span style={labelStyle}>Bike rental (A)</span>
              <span style={valueStyle}>₹ {localState.formData.payment.bikeRental || 0}</span>
            </div>
            {isAccessoryPriceRequired && (
              <div style={rowStyle}>
                <span style={labelStyle}>Accessory rental (B)</span>
                <span style={valueStyle}>₹ {localState.formData.payment.accessoryRental || 0}</span>
              </div>
            )}
            {isDefaultDiscountRequired && (
              <div style={rowStyle}>
                <span style={labelStyle}>Location discount (C)</span>
                <span style={{ ...valueStyle, color: '#558b2f' }}>- ₹ {localState.formData.payment.defaultDiscount || 0}</span>
              </div>
            )}
            {isVendorDiscountRequired && (
              <div style={inputRowStyle}>
                <div style={inputLabelStyle}>Dealer discount (D)</div>
                <GobikesField
                  type={GbkFieldType.NUMBER}
                  editMode={true}
                  value={localState.formData.payment.vendorDiscount || 0}
                  updateFn={(v: number) => { if (v >= 0) updatePaymentDetails({ vendorDiscount: Number(v) }); }}
                  inputProps={{ startAdornment: <InputAdornment position="start" style={{ color: Colors.primary }}><span> - ₹</span></InputAdornment>, className: 'primary-text-input' }}
                  gbkFieldSx={{ width: '100%' }}
                />
              </div>
            )}
            <div style={{ ...rowStyle, borderTop: '2px solid #eee', background: '#fafff5' }}>
              <span style={{ ...labelStyle, fontWeight: 700, color: '#222' }}>
                Final customer amount <span style={{ fontSize: 11, fontWeight: 400, color: '#999' }}>{calculationExpression}</span>
              </span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#558b2f' }}>₹ {localState.formData.payment.finalAmountPaid || 0}</span>
            </div>
            <div style={inputRowStyle}>
              <div style={inputLabelStyle}>Deposit (collected offline)</div>
              <GobikesField
                type={GbkFieldType.NUMBER}
                editMode={true}
                value={localState.formData.payment.deposit || 0}
                updateFn={(v: number) => { if (v >= 0) updatePaymentDetails({ deposit: Number(v) }); }}
                inputProps={{ startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }}
                gbkFieldSx={{ width: '100%' }}
              />
            </div>
            <div style={inputRowStyle}>
              <div style={inputLabelStyle}>Payment Method</div>
              <GobikesField
                type={GbkFieldType.SELECT}
                editMode={true}
                value={localState.formData.payment.paymentMethod || ''}
                validateFn={falsyValidationFn()}
                updateFn={(paymentMethod: any) => { updatePaymentDetails({ paymentMethod }); }}
                selectOptions={[
                  { display: 'ONLINE', value: PaymentMode.ONLINE },
                  ...([VendorPaymentMode.OFFLINE_ONLY, VendorPaymentMode.PREPAID_AND_OFFLINE, VendorPaymentMode.POSTPAID_AND_OFFLINE, VendorPaymentMode.ALL_ONLINE_AND_OFFLINE].includes(profile.paymentMode) ? [{ display: 'OFFLINE', value: PaymentMode.OFFLINE }] : []),
                ]}
                gbkFieldSx={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Deductions card */}
          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Deductions</div>
            <div style={rowStyle}>
              <span style={labelStyle}>GoWheelo Commission (incl. GST)</span>
              <span style={valueStyle}>₹ {(localState.formData.payment.goBikesCommission || 0)}</span>
            </div>
            <div style={rowStyle}>
              <span style={labelStyle}>Payment Gateway Charges</span>
              <span style={valueStyle}>₹ {(localState.formData.payment.convFees || 0)}</span>
            </div>
            <div style={{ ...rowStyle, borderTop: '2px solid #eee', background: '#fafafa' }}>
              <span style={{ ...labelStyle, fontWeight: 700, color: '#222' }}>Final Deductions</span>
              <span style={{ ...valueStyle, color: '#c0392b' }}>₹ {(localState.formData.payment.finalDeductions || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Settlement card */}
          <div style={cardStyle}>
            <div style={sectionHeaderStyle}>Settlement</div>
            <div style={{ ...rowStyle, background: '#fafff5' }}>
              <span style={{ ...labelStyle, fontWeight: 700, color: '#222' }}>
                Final Payable Amount
                <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>Total Rental - Final Deductions</div>
              </span>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#558b2f' }}>₹ {(localState.formData.payment.finalPayableAmount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <Fragment>
        {([
          'Payment Details',
          {
            name: 'Bike rental (A)',
            type: GbkFieldType.NUMBER,
            value: localState.formData.payment.bikeRental || 0,
            disabled: true,
            validateFn: greaterThanValueValidationFn(0, "Rental amount can not be 0"),
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
          },
          ...(isAccessoryPriceRequired ? [
            {
              name: 'Accessory rental (B)',
              type: GbkFieldType.NUMBER,
              value: localState.formData.payment.accessoryRental || 0,
              disabled: true,
              inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
            }
          ] : []),
          ...(isDefaultDiscountRequired ? [
            {
              name: 'Discount offer active on selected location (C)',
              type: GbkFieldType.NUMBER,
              value: localState.formData.payment.defaultDiscount || 0,
              disabled: true,
              inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
            }
          ] : []),
          ...(isVendorDiscountRequired ? [
            {
              name: 'Discount offered by dealer (D)',
              type: GbkFieldType.NUMBER,
              value: localState.formData.payment.vendorDiscount || 0,
              updateFn: (v: number) => { if (v >= 0) updatePaymentDetails({ vendorDiscount: Number(v) }); },
              inputProps: {
                startAdornment: <InputAdornment position="start" style={{ color: Colors.primary }}><span> - ₹</span></InputAdornment>,
                className: 'primary-text-input'
              }
            }
          ] : []),
          {
            //   name: 'Discount offered by GoBikes (E)',
            //   type: GbkFieldType.NUMBER,
            //   value: 0,
            //   disabled: true,
            //   inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
            // }, {
            name: <b style={{ lineHeight: '22px', display: 'inherit' }}>
              Final amount to be paid by customer{isPhone ? <br /> : ''}&nbsp;
              {calculationExpression}
            </b>,
            type: GbkFieldType.NUMBER,
            value: localState.formData.payment.finalAmountPaid || 0,
            disabled: true,
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
          }, {
            name: 'Deposit (To be collected offline)',
            type: GbkFieldType.NUMBER,
            value: localState.formData.payment.deposit || 0,
            updateFn: (v: number) => { if (v >= 0) updatePaymentDetails({ deposit: Number(v) }); },
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> }
          }, {
            name: 'Payment Method',
            type: GbkFieldType.SELECT,
            value: localState.formData.payment.paymentMethod || '',
            validateFn: falsyValidationFn(),
            disableClearable: true,
            updateFn: (paymentMethod: any) => { updatePaymentDetails({ paymentMethod }); },
            selectOptions: [
              { display: 'ONLINE', value: PaymentMode.ONLINE },
              ...([
                VendorPaymentMode.OFFLINE_ONLY,
                VendorPaymentMode.PREPAID_AND_OFFLINE,
                VendorPaymentMode.POSTPAID_AND_OFFLINE,
                VendorPaymentMode.ALL_ONLINE_AND_OFFLINE
              ].includes(profile.paymentMode) ? [{ display: 'OFFLINE', value: PaymentMode.OFFLINE }] : []),
            ]
          },
          'Deductions',
          {
            name: 'GoWheelo Commission (including GST)',
            type: GbkFieldType.NUMBER,
            value: localState.formData.payment.goBikesCommission || 0,
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> },
            disabled: true,
          }, {
            name: 'Payment Gateway Charges',
            type: GbkFieldType.NUMBER,
            value: localState.formData.payment.convFees || 0,
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> },
            disabled: true,
          }, {
            name: <b>Final Deductions</b>,
            type: GbkFieldType.NUMBER,
            value: (localState.formData.payment.finalDeductions || 0).toFixed(2),
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> },
            disabled: true,
          },
          'Settlement Details',
          {
            name: <span style={{ lineHeight: '20px', display: 'inline-block' }}>Final Payable Amount<span style={{ color: Colors.greyDark3 }}>{isPhone ? <br /> : ''} (Total Rental Amount - Final Deductions)</span></span>,
            type: GbkFieldType.NUMBER,
            value: (localState.formData.payment.finalPayableAmount || 0).toFixed(2),
            inputProps: { startAdornment: <InputAdornment position="start"> &nbsp; ₹</InputAdornment> },
            disabled: true,
          }
        ] as ((GobikesFieldProps & PaymentDetailsFieldInfo) | string)[]).map((f, i) => (
          <Fragment key={i}>
            <Box className="gbk-col" sx={{ ...styles.spacer, paddingBottom: { xs: '4px', md: '20px' } }}></Box>
            {(typeof f === 'string') ? (
              <Box className="gbk-col field-col no-pad-bot" sx={styles.col}>
                {isPhone ? <Box sx={{ height: '4px' }} /> : <br />}
                <Box className="gbk-col" sx={styles.col}>
                  {isPhone
                    ? <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{f}</Typography>
                    : <Typography variant="h6" className="bold">{f}</Typography>
                  }
                </Box>
              </Box>
            ) : (
              <Box className="gbk-col field-col no-pad-bot" sx={{ ...styles.col, paddingBottom: { xs: '4px', md: undefined } }}>
                <Box className="field-name" sx={styles.field}>{f.name}</Box>
                <Box className="field-value" sx={styles.value}>
                  <GobikesField
                    type={f.type}
                    editMode={true}
                    disabled={f.disabled}
                    value={f.value}
                    updateFn={f.updateFn}
                    validateFn={f.validateFn}
                    inputProps={f.inputProps}
                    selectOptions={f.selectOptions}
                  />
                </Box>
              </Box>
            )}
          </Fragment>
        ))}
        <Box sx={{ height: { xs: '16px', md: '40px' } }} />
      </Fragment>
    )
  }

  // Send order request
  function renderOrderRequest() {
    return <DashboardLoader
      {...localState}
      styles={{
        containerSx: {
          mt: { xs: 0, md: '40px' },
          width: 'calc(100%)',
          height: 'auto',
          paddingTop: '200px',
          paddingBottom: '200px',
          boxShadow: 'none'
        },
      }}
      customIcon={<DoneAll className="icon" style={{ color: Colors.primary }} />}
      resetError={localState.error ? handleBack : undefined}
      resetErrorText={'Back'}
      resetErrorIcon={<ArrowBack />}
      resetErrorIconPrefix={true}
    />
  }

  // Return DOM
  return renderContent();
}
